import './privacy.css';

const PrivacyPolicy = () => {
    return (
        <div className="body-div">
            <div className="policy-header">
                <div className="wrap text-center">
                    <p className="eyebrow">Legal</p>
                    <h1>Privacy Policy at Twigaa.ai</h1>
                    <p className="lede policy-effective">Effective Date: 01-Jan-2025</p>
                </div>
            </div>

            <main className="wrap policy-body mt-5">
                <p className="policy-intro">
                    Twigaa ("we", "our", "us") is committed to protecting your privacy. This Privacy Policy explains
                    how we collect, use, and safeguard your information when you use our platform.
                </p>

                <section className="policy-section">
                    <span className="policy-num">01</span>
                    <h2>Information We Collect</h2>
                    <p>We may collect the following types of information:</p>
                    <ul className="policy-list">
                        <li><strong>Account Information:</strong> Name, email address, company details</li>
                        <li><strong>Operational Data:</strong> Travel itineraries, vouchers, booking details</li>
                        <li><strong>Communication Data:</strong> Emails sent through Twigaa on your behalf</li>
                    </ul>
                    <p className="policy-subhead"><strong>Google Account Data (if connected):</strong></p>
                    <ul className="policy-list">
                        <li>Email address</li>
                        <li>Permission to send emails via Gmail</li>
                    </ul>
                </section>

                <section className="policy-section">
                    <span className="policy-num">02</span>
                    <h2>How We Use Your Information</h2>
                    <p>We use your information to:</p>
                    <ul className="policy-list">
                        <li>Provide and operate the Twigaa platform</li>
                        <li>Generate and send travel vouchers and communications</li>
                        <li>Improve system performance and user experience</li>
                        <li>Provide customer support</li>
                    </ul>
                </section>

                <section className="policy-section">
                    <span className="policy-num">03</span>
                    <h2>Gmail Integration & Google User Data</h2>
                    <h5>Email Integration and CRM Communication</h5>
                    <p>Twigaa provides optional email integration features that allow users to connect their email accounts (such as Gmail or Google Workspace) to enable seamless communication and customer relationship management directly within the platform.</p>

                    <h5>Scope of Access</h5>
                    <p>With your explicit authorization, Twigaa may request permission to:</p>
                    <ul className="policy-list">
                        <li>Send emails on your behalf, including vouchers, invoices, quotations, and operational communications</li>
                        <li>Read and process email messages and replies associated with communications initiated or managed within Twigaa</li>
                        <li>Access and store relevant email metadata, including sender, recipient, subject, timestamps, and thread identifiers</li>
                        <li>Display and organize communication history in relation to leads, bookings, customers, and partners</li>
                        <li>Enable users to respond to emails directly from within the Twigaa platform</li>
                    </ul>

                    <h5>Limited Use of Email Data</h5>
                    <p>Twigaa's access to email data is strictly limited to what is necessary to provide the above functionality. The system is designed to focus on emails relevant to user-initiated workflows, such as customer inquiries, booking-related communication, and partner coordination.
                    <br />Twigaa does not perform automated analysis of unrelated emails outside the context of user-initiated workflows.
                    <br />Twigaa does not access, read, or process email content for purposes unrelated to its core product functionality.</p>

                    <h5>Data Usage Restrictions</h5>
                    <p>Twigaa does not use email data for:</p>
                    <ul className="policy-list">
                        <li>Advertising or marketing purposes</li>
                        <li>User profiling or behavioral tracking outside the platform's intended functionality</li>
                        <li>Selling, renting, or sharing email data with third parties</li>
                    </ul>
                    <p>Email data is used solely to deliver and improve Twigaa's core features, including communication tracking, workflow management, and operational efficiency.</p>

                    <h5>Data Storage and Security</h5>
                    <p>Email metadata and relevant content required for functionality may be securely stored within Twigaa's infrastructure. Twigaa implements industry-standard security practices to protect user data from unauthorized access, disclosure, or misuse.
                    <br />Sensitive authentication credentials (such as OAuth tokens) are securely stored and handled in accordance with industry best practices.</p>

                    <h5>User Control and Revocation</h5>
                    <p>Users retain full control over their email integration. You may revoke Twigaa's access to your email account at any time by:</p>
                    <ul className="policy-list">
                        <li>Disconnecting your email account within Twigaa settings</li>
                        <li>Revoking access via your email provider's security settings</li>
                    </ul>
                    <p>Upon revocation, Twigaa will no longer be able to send or read emails on your behalf.</p>

                    <h5>Compliance with Google API Services User Data Policy</h5>
                    <p>Twigaa's use of information received from Google APIs adheres to the Google API Services User Data Policy, including the Limited Use requirements. Twigaa only accesses and uses Google user data to provide user-facing features and does not transfer this data to third parties except as necessary to provide and improve these features.</p>
                </section>

                <section className="policy-section">
                    <span className="policy-num">04</span>
                    <h2>Data Storage & Security</h2>
                    <ul className="policy-list">
                        <li>Your data is stored securely using industry-standard practices</li>
                        <li>We implement safeguards to prevent unauthorized access</li>
                        <li>Access to sensitive data is restricted and monitored</li>
                    </ul>
                </section>

                <section className="policy-section">
                    <span className="policy-num">05</span>
                    <h2>Data Sharing</h2>
                    <p>We do <strong>not sell, rent, or trade your personal data</strong>.</p>
                    <p>We may share data only:</p>
                    <ul className="policy-list">
                        <li>With your explicit action (e.g., sending emails to partners)</li>
                        <li>To comply with legal obligations</li>
                    </ul>
                </section>

                <section className="policy-section">
                    <span className="policy-num">06</span>
                    <h2>Data Retention</h2>
                    <p>We retain your data only as long as necessary to:</p>
                    <ul className="policy-list">
                        <li>Provide our services</li>
                        <li>Comply with legal requirements</li>
                    </ul>
                </section>

                <section className="policy-section">
                    <span className="policy-num">07</span>
                    <h2>Your Rights</h2>
                    <p>You may:</p>
                    <ul className="policy-list">
                        <li>Request access to your data</li>
                        <li>Request correction or deletion</li>
                        <li>Disconnect your Google account at any time</li>
                    </ul>
                </section>

                <section className="policy-section">
                    <span className="policy-num">08</span>
                    <h2>Third-Party Services</h2>
                    <p>Twigaa integrates with third-party services such as Google APIs. Use of such services is subject to their respective privacy policies.</p>
                </section>

                <section className="policy-section">
                    <span className="policy-num">09</span>
                    <h2>Changes to This Policy</h2>
                    <p>We may update this policy periodically. Updates will be reflected on this page.</p>
                </section>

                <section className="policy-section">
                    <span className="policy-num">10</span>
                    <h2>Contact Us</h2>
                    <p>For any questions, contact us at:</p>
                    <ul className="policy-list policy-contact-list mb-0 pb-5">
                        <li>
                            <a href="mailto:support@twigaa.ai" className="hanger-tag">
                                <span className="material-symbols-outlined">mail</span> support@twigaa.ai
                            </a>
                        </li>
                        <li>
                            <a href="https://twigaa.ai" target="_blank" rel="noopener noreferrer" className="hanger-tag">
                                <span className="material-symbols-outlined">language</span> https://twigaa.ai
                            </a>
                        </li>
                    </ul>
                </section>
            </main>
        </div>
    );
};
export default PrivacyPolicy;